import { useState } from "react"
import { BatchTrackingResponse } from "../interface/TrackingInterface"
import { CsvService } from "../services/CsvService"
import loginService from "../services/LoginService"
import TrackingService from "../services/TrackinService"
import { useToast } from "./useToast"

export interface BatchItem {
    cpfCnpj: string
    invoiceNumber: string
    serie: string
    line: number
}

export interface BatchResult {
    item: BatchItem
    data?: BatchTrackingResponse
    error?: string
    success: boolean
}

type ProcessingType = "last_occurrence" | "all_occurrences"

const MESSAGES = {
    EMPTY_BATCH: "Por favor, preencha a lista de consultas",
    INVALID_FORMAT: "Formato inválido. Use: NumeroNota,Serie (um por linha)",
    PROCESSING: "Processando consultas...",
    SUCCESS: "Arquivo CSV gerado com sucesso!",
    PARTIAL_SUCCESS: "Algumas consultas falharam. Verifique o arquivo CSV.",
    ERROR: "Erro ao processar as consultas"
}

export const useBatchProcessing = () => {
    const [batchInput, setBatchInput] = useState("")
    const [isProcessing, setIsProcessing] = useState(false)
    const [results, setResults] = useState<BatchResult[]>([])
    const [processingType, setProcessingType] =
        useState<ProcessingType>("last_occurrence")

    const { showErrorToast, showSuccessToast } = useToast()

    // Debug log para verificar mudanças no processingType
    console.log("useBatchProcessing - processingType:", processingType)

    // Wrapper para setProcessingType com debug
    const handleSetProcessingType = (type: ProcessingType) => {
        console.log("useBatchProcessing - setProcessingType called with:", type)
        setProcessingType(type)
    }

    const parseBatchInput = (input: string): BatchItem[] => {
        const lines = input
            .trim()
            .split("\n")
            .filter((line) => line.trim())
        const items: BatchItem[] = []

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim()
            const parts = line.split(",")

            if (parts.length !== 2) {
                throw new Error(
                    `Linha ${i + 1}: Formato inválido. Use: NumeroNota,Serie`
                )
            }

            const invoiceNumber = parts[0].trim()
            const serie = parts[1].trim()

            if (!invoiceNumber || !serie) {
                throw new Error(
                    `Linha ${i + 1}: Número da Nota e Série são obrigatórios`
                )
            }

            items.push({
                cpfCnpj: "", // Não utilizado mais, mantido para compatibilidade
                invoiceNumber,
                serie,
                line: i + 1
            })
        }

        return items
    }

    const fetchBatchItems = async (
        items: BatchItem[]
    ): Promise<BatchResult[]> => {
        try {
            const token = loginService.getAuthToken()
            if (!token) {
                throw new Error("Token de autenticação não encontrado")
            }

            const trackingItems = items.map((item) => ({
                numero: item.invoiceNumber,
                serie: item.serie
            }))

            let batchResponse: any[]
            if (processingType === "all_occurrences") {
                console.log("getBatchAllOccurrences")
                batchResponse = await TrackingService.getBatchAllOccurrences(
                    trackingItems,
                    token
                )
            } else {
                console.log("getBatchInformation")
                batchResponse = await TrackingService.getBatchInformation(
                    trackingItems,
                    token
                )
            }
            console.log("batchResponse", batchResponse.length)

            if (processingType === "all_occurrences") {
                // Para todas as ocorrências, precisamos agrupar os resultados por nota fiscal
                // pois a API retorna um item por ocorrência, não por nota fiscal
                const groupedResults = new Map<string, any>()

                batchResponse.forEach((responseItem) => {
                    const key = `${responseItem.notafiscal.numero}-${responseItem.notafiscal.serie}`
                    if (!groupedResults.has(key)) {
                        // Primeira ocorrência desta nota fiscal
                        groupedResults.set(key, {
                            notafiscal: responseItem.notafiscal,
                            ocorrencias: responseItem.ocorrencias || []
                        })
                    } else {
                        // Adiciona ocorrências à nota fiscal existente
                        const existing = groupedResults.get(key)!
                        existing.ocorrencias.push(
                            ...(responseItem.ocorrencias || [])
                        )
                    }
                })

                return items.map((item) => {
                    const key = `${item.invoiceNumber}-${item.serie}`
                    const groupedData = groupedResults.get(key)

                    if (groupedData) {
                        return {
                            item,
                            data: groupedData,
                            success: true
                        }
                    } else {
                        return {
                            item,
                            error: "Dados não encontrados",
                            success: false
                        }
                    }
                })
            } else {
                // Para última ocorrência, mapeamento direto por índice
                return items.map((item, index) => {
                    console.log("batchResponse[index]", batchResponse[index])
                    if (batchResponse[index]) {
                        return {
                            item,
                            data: batchResponse[index],
                            success: true
                        }
                    } else {
                        return {
                            item,
                            error: "Dados não encontrados",
                            success: false
                        }
                    }
                })
            }
        } catch (error: any) {
            // Se falhar o endpoint de lote, propagar o erro
            throw error
        }
    }

    const processBatch = async () => {
        if (!batchInput.trim()) {
            showErrorToast(MESSAGES.EMPTY_BATCH)
            return
        }

        try {
            const items = parseBatchInput(batchInput)

            setIsProcessing(true)

            const batchResults = await fetchBatchItems(items)
            console.log("batchResults22", batchResults.length)
            setResults(batchResults)

            CsvService.generateCSV(batchResults, processingType)

            const successCount = batchResults.filter((r) => r.success).length
            const totalCount = batchResults.length

            if (successCount === totalCount) {
                showSuccessToast(MESSAGES.SUCCESS)
            } else if (successCount > 0) {
                showSuccessToast(MESSAGES.PARTIAL_SUCCESS)
            } else {
                showErrorToast(MESSAGES.ERROR)
            }
        } catch (error: any) {
            showErrorToast(error.message || MESSAGES.ERROR)
        } finally {
            setIsProcessing(false)
        }
    }

    const clearData = () => {
        setBatchInput("")
        setResults([])
        setProcessingType("last_occurrence")
    }

    const getInputLineCount = () => {
        return batchInput.split("\n").filter((line) => line.trim()).length
    }

    return {
        // State
        batchInput,
        setBatchInput,
        isProcessing,
        results,
        processingType,
        setProcessingType: handleSetProcessingType,

        // Actions
        processBatch,
        clearData,

        // Utils
        getInputLineCount
    }
}
